import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { REQUEST_PASSWORD_RESET } from '../graphql/mutations';
import { toast } from 'react-hot-toast';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [requestReset] = useMutation(REQUEST_PASSWORD_RESET);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple email validation
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await requestReset({ 
        variables: { 
          input: { email } 
        } 
      });
      
      setSubmitted(true);
      toast.success('If an account exists with this email, you will receive reset instructions');
    } catch (error) {
      // Always show the same message even when there's an error,
      // to prevent user enumeration attacks
      setSubmitted(true);
      console.error('Password reset error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="flex min-h-screen bg-gray-100">
      <div className="m-auto max-w-md w-full p-8 bg-white rounded-lg shadow-md">
        <div className="text-center mb-8">
          <img 
            src="/images/logo.png" 
            alt="Sweetgrass Properties Logo" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">Reset Your Password</h1>
        </div>
        
        {submitted ? (
          <div className="text-center">
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <p className="text-green-800">
                If an account exists with this email, we've sent instructions to reset your password.
              </p>
              <p className="text-green-800 mt-2">
                Please check your email inbox and spam folder.
              </p>
            </div>
            
            <Link 
              to="/login" 
              className="text-sky-600 hover:text-sky-800 font-medium"
            >
              Return to Login
            </Link>
          </div>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                  required
                />
              </div>
              
              <button
                type="submit"
                className="w-full bg-sky-600 text-white font-medium py-2 px-4 rounded-md hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
              </button>
              
              <div className="mt-4 text-center">
                <Link 
                  to="/login" 
                  className="text-sky-600 hover:text-sky-800 text-sm font-medium"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
} 